import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, ArrowUpRight } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { downloadFile } from "@/lib/training";
import { formatDate } from "@/lib/constants";
import { PageHeader, RoleBadge, EmptyState } from "@/components/Badges";
import { StatTile, ProgressBar } from "@/components/training/TrainingBits";
import { Highlights } from "@/components/training/Highlights";

const Breakdown = ({ title, rows, testId }) => (
  <section className="rounded-2xl border border-lasso-line bg-white p-6" data-testid={testId}>
    <h2 className="mb-5 font-display text-sm font-bold uppercase tracking-[0.14em]">{title}</h2>
    {rows.length === 0 && <p className="text-sm text-muted-foreground">No assigned training yet.</p>}
    <div className="space-y-4">
      {rows.map((r) => (
        <div key={r.id} data-testid={`${testId}-row`}>
          <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
            <span className="truncate font-medium">{r.name}</span>
            <span className="shrink-0 text-xs text-muted-foreground"><strong className="text-lasso-ink">{r.pct}%</strong> · {r.completed}/{r.assigned}{r.overdue ? <span className="ml-1 font-semibold text-rose-600">· {r.overdue} overdue</span> : null}</span>
          </div>
          <ProgressBar pct={r.pct} />
        </div>
      ))}
    </div>
  </section>
);

export default function Reports() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/overview").then((r) => setData(r.data));
  }, []);

  const exportCsv = async () => {
    try {
      await downloadFile("/reports/export.csv", `lasso-training-report-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data) return null;
  const t = data.totals;
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Progress" title="Training progress" description={`Scope: ${data.scope_label}${data.scope === "community_manager" ? " (read-only)" : ""}`}
        actions={<button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="reports-export-csv"><Download className="h-4 w-4" /> Export CSV</button>} />
      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatTile label="Completion" value={`${t.pct}%`} testId="reports-stat-completion" />
        <StatTile label="People" value={t.people} testId="reports-stat-people" />
        <StatTile label="Assigned items" value={t.assigned} testId="reports-stat-assigned" />
        <StatTile label="Overdue" value={t.overdue} tone="danger" testId="reports-stat-overdue" />
        <StatTile label="Certificates" value={t.certificates} testId="reports-stat-certificates" />
      </div>
      <Highlights />
      <div className="mb-8 grid gap-6 lg:grid-cols-3">
        {data.scope === "admin" && <Breakdown title="By region" rows={data.by_region} testId="reports-by-region" />}
        {data.scope !== "community_manager" && <Breakdown title="By community" rows={data.by_community} testId="reports-by-community" />}
        <Breakdown title="By course / SOP" rows={data.by_item} testId="reports-by-item" />
      </div>

      <section className="mb-8">
        <h2 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.14em]">Overdue ({data.overdue.length})</h2>
        {data.overdue.length === 0 ? <EmptyState title="Nothing overdue" testId="reports-overdue-empty" /> : (
          <div className="overflow-hidden rounded-2xl border border-rose-200 bg-white">
            <Table data-testid="reports-overdue-table">
              <TableHeader><TableRow><TableHead>Person</TableHead><TableHead>Item</TableHead><TableHead>Community</TableHead><TableHead>Due</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.overdue.map((o, i) => (
                  <TableRow key={i}>
                    <TableCell><Link to={`/reports/people/${o.user_id}`} className="font-semibold hover:underline">{o.user_name}</Link></TableCell>
                    <TableCell className="text-sm">{o.title} <span className="text-xs text-muted-foreground">({o.item_type === "course" ? "Course" : "SOP"})</span></TableCell>
                    <TableCell className="text-sm">{o.community_name || o.region_name || "—"}</TableCell>
                    <TableCell className="text-sm font-semibold text-rose-600">{formatDate(o.due_date + "T12:00:00")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.14em]">People</h2>
        <div className="overflow-hidden rounded-2xl border border-lasso-line bg-white">
          <Table data-testid="reports-people-table">
            <TableHeader><TableRow><TableHead>Person</TableHead><TableHead>Role</TableHead><TableHead>Assignment</TableHead><TableHead>Completion</TableHead><TableHead>Overdue</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {data.people.map((p) => (
                <TableRow key={p.id} data-testid={`reports-person-${p.id}`}>
                  <TableCell><p className="font-semibold">{p.name}</p><p className="text-xs text-muted-foreground">{p.email}</p></TableCell>
                  <TableCell><RoleBadge role={p.role} /></TableCell>
                  <TableCell className="text-sm">{p.community_name || p.region_name || "Company-wide"}</TableCell>
                  <TableCell className="w-48"><div className="flex items-center gap-3"><ProgressBar pct={p.pct} /><span className="text-xs font-semibold">{p.completed}/{p.assigned}</span></div></TableCell>
                  <TableCell className={`text-sm ${p.overdue ? "font-semibold text-rose-600" : "text-muted-foreground"}`}>{p.overdue}</TableCell>
                  <TableCell className="text-right"><Link to={`/reports/people/${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline" data-testid={`reports-person-link-${p.id}`}>Record <ArrowUpRight className="h-3.5 w-3.5" /></Link></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}
